import { getStudentGalleryBucketName } from './supabase';

export function sanitizeStudentGalleryName(studentName) {
  const raw = String(studentName || 'student').trim();
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'student').slice(0, 80);
}

export function sanitizeGalleryFilename(name) {
  const raw = String(name || '').replace(/^.*[/\\]/, '');
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'photo').slice(0, 180);
}

/** Storage object key: [studentName]_[fileName] (unique timestamp inserted before extension). */
export function buildStudentGalleryStoragePath(studentName, fileName) {
  const safeName = sanitizeStudentGalleryName(studentName);
  const safeFile = sanitizeGalleryFilename(fileName);
  const dot = safeFile.lastIndexOf('.');
  const base = dot > 0 ? safeFile.slice(0, dot) : safeFile;
  const ext = dot > 0 ? safeFile.slice(dot) : '.jpg';
  const stamp = Date.now();
  return `${safeName}_${base}_${stamp}${ext}`;
}

export function studentGalleryPrefix(studentName) {
  return `${sanitizeStudentGalleryName(studentName)}_`;
}

export async function listStudentGalleryImages(supabase, studentName) {
  const bucket = getStudentGalleryBucketName();
  const prefix = studentGalleryPrefix(studentName);
  const { data, error } = await supabase.storage.from(bucket).list('', {
    limit: 200,
    sortBy: { column: 'created_at', order: 'desc' },
  });
  if (error) {
    throw new Error(error.message || 'Could not list gallery images');
  }
  return (data || [])
    .filter((item) => item?.name && item.name.startsWith(prefix))
    .map((item) => {
      const { data: pub } = supabase.storage.from(bucket).getPublicUrl(item.name);
      return {
        name: item.name,
        url: pub?.publicUrl || '',
      };
    })
    .filter((item) => item.url);
}

export async function uploadStudentGalleryImage(
  supabase,
  studentName,
  { uri, fileName, uriToArrayBuffer, guessMimeFromName }
) {
  const bucket = getStudentGalleryBucketName();
  const path = buildStudentGalleryStoragePath(studentName, fileName);
  const body = await uriToArrayBuffer(uri);
  const { error } = await supabase.storage.from(bucket).upload(path, body, {
    contentType: guessMimeFromName(fileName),
    upsert: false,
  });
  if (error) {
    throw new Error(error.message || 'Gallery upload failed');
  }
  const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
  const publicUrl = pub?.publicUrl;
  if (!publicUrl) {
    throw new Error('Could not get public URL for gallery image');
  }
  return { path, url: publicUrl };
}
